import {
    AbstractControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import {
    AssetType,
    defaultValueFor,
} from '@components';
import {
    IAsset,
    IAssetRobot,
    IAssetType,
} from '@models';
import { ISuggestValue } from '@uipath/angular/components/ui-suggest';
import UiValidators, {
    INT32_MAX,
    INT32_MIN,
    UI_ASSET_PASSWORD_MAX_LENGTH,
    UI_ASSET_TEXT_MAX_LENGTH,
    UI_ASSET_USERNAME_MAX_LENGTH,
} from '@validators';

import { RobotAssetSource } from './robot-asset-source';

export const validatorsFor = (type: IAssetType, isNewEntry = true, readonly?: boolean, robot?: boolean) => {
    const validators = [];
    switch (type) {
        case AssetType.Text:
            validators.push(
                UiValidators.required({
                    allowWhitespace: false,
                }),
                Validators.maxLength(UI_ASSET_TEXT_MAX_LENGTH),
            );
            break;
        case AssetType.Integer:
            validators.push(
                Validators.required,
                UiValidators.regex('^-?[0-9]+$', true),
                UiValidators.range(INT32_MIN, INT32_MAX, true),
            );
            break;
        case AssetType.Bool:
            validators.push(UiValidators.regex(/true|false/));
            break;
        case AssetType.Credential:
            if (readonly) {
                validators.push(
                    Validators.maxLength(UI_ASSET_TEXT_MAX_LENGTH),
                );
                if (robot) {
                    validators.push(UiValidators.required({
                        allowWhitespace: false,
                    }));
                }
                break;
            }
            validators.push(
                UiValidators.required({
                    property: 'username',
                    allowWhitespace: false,
                }),
                UiValidators.maxLength(UI_ASSET_USERNAME_MAX_LENGTH, {
                    property: 'username',
                }),
                UiValidators.maxLength(UI_ASSET_PASSWORD_MAX_LENGTH, {
                    property: 'password',
                }),
            );
            if (isNewEntry) {
                validators.push(UiValidators.required({
                    property: 'password',
                    allowWhitespace: false,
                }));
            }
            break;
    }
    return Validators.compose(validators);
};

export const resetForm = (
    fromGroup: FormGroup,
    controlName: string,
    type: IAssetType,
    isNewEntry?: boolean,
    readonly?: boolean,
    robot?: boolean,
    value?: any) => {
        fromGroup.get(controlName)!.clearValidators();
        value = value || defaultValueFor(type, null, readonly);
        fromGroup.get(controlName)!.setValue(value);
        fromGroup.get(controlName)!.setValidators(validatorsFor(type, !!isNewEntry, readonly, robot));
        fromGroup.get(controlName)!.markAsPristine();
        fromGroup.get(controlName)!.updateValueAndValidity();
};

export const AssetCredentialValidator =
    (controlName: string,
    fetchType: () => IAssetType,
    fetchAsset: () => IAsset | IAssetRobot,
    fetchReadOnly: () => boolean | undefined) => {
        return (group: FormGroup) => {
            const model = fetchAsset() || {} as IAsset;
            if (
                fetchType() !== AssetType.Credential ||
                fetchReadOnly() ||
                model.id < 0
            ) { return; }

            const control = group.get(controlName)!;

            // if the username was changed for an existing robot asset entry
            // notify the user to enter a new password
            if (
                !!model.credentialUsername &&
                control.value.username !== model.credentialUsername &&
                !control.value.password
            ) {
                return {
                    invalidPassword: true,
                };
            }

            // if the robot was changed for an existing robot asset entry
            // notify the user to enter a new password
            if (
                !model.credentialUsername &&
                !control.value.password
            ) {
                return {
                    robotChanged: true,
                };
            }

            return null;
        };
};

export const RobotValidator = (c: AbstractControl) => {
    if (!c.value.length) {
        return {
            invalidRobot: true,
        };
    }
    return null;
};

export const RobotDuplicateValidator = (assets: RobotAssetSource) => (c: AbstractControl) => {
    if (!c.value.length) { return null; }

    const robot = c.value[0] as ISuggestValue;

    if (!assets.isAssetBoundToRobot(robot.id as number)) { return null; }

    return {
        duplicateRobot: true,
    };
};

